import { Fragment, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  NotificationLogItem,
  resolveAutomationName,
  NotificationAutomation,
} from "./notificationsListQuery";
import {
  formatNotificationDate,
  notificationChannelBadge,
  notificationStatusBadge,
} from "./notificationListDisplay";

type Props = {
  logs: NotificationLogItem[];
  automationsByKey: Map<string, NotificationAutomation>;
};

const HEADER_CELL =
  "whitespace-nowrap border-x-0 border-b-0 border-l-0 border-r-0 border-t-0 px-1.5 py-2 text-left text-xs font-semibold text-slate-700";
const BODY_CELL =
  "border-x-0 border-b-0 border-l-0 border-r-0 border-t-0 px-1.5 py-2 text-[13px] text-slate-600";

export function NotificationsListTable({ logs, automationsByKey }: Props) {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<number>>(new Set());

  function toggleRowExpansion(rowId: number) {
    setExpandedRowIds((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }

  return (
    <div className="relative mt-4 overflow-x-auto overflow-hidden rounded-t-xl">
      <table className="min-w-[1080px] w-full border-separate border-spacing-0 border-none text-[13px] font-[inherit]">
        <thead className="bg-slate-50">
          <tr>
            <th className={`${HEADER_CELL} rounded-tl-xl`}>Destinatário</th>
            <th className={HEADER_CELL}>Canal</th>
            <th className={HEADER_CELL}>Automação</th>
            <th className={HEADER_CELL}>Status</th>
            <th className={HEADER_CELL}>Assunto / Template</th>
            <th className={`${HEADER_CELL} rounded-tr-xl`}>Enviado em</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log, rowIndex) => {
            const channelBadge = notificationChannelBadge(log.channel);
            const statusBadge = notificationStatusBadge(log.status);
            const automationName = resolveAutomationName(log.automation, automationsByKey);
            const isExpanded = expandedRowIds.has(log.id);
            const hasError = log.status === "failed" && Boolean(log.error_message);

            return (
              <Fragment key={log.id}>
                <tr
                  className={`transition ${
                    rowIndex % 2 === 0
                      ? "bg-white hover:bg-slate-50"
                      : "bg-slate-50/90 hover:bg-slate-100/90"
                  }`}
                >
                  <td className={`${BODY_CELL} max-w-[220px] font-medium text-slate-700`}>
                    <span className="line-clamp-2 break-all" title={log.recipient}>
                      {log.recipient || "—"}
                    </span>
                  </td>
                  <td className={`${BODY_CELL} whitespace-nowrap`}>
                    <span
                      className={`inline-flex w-fit items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight ${channelBadge.className}`}
                    >
                      {channelBadge.label}
                    </span>
                  </td>
                  <td className={`${BODY_CELL} max-w-[220px]`}>
                    <span className="line-clamp-2" title={automationName}>
                      {automationName}
                    </span>
                  </td>
                  <td className={`${BODY_CELL} whitespace-nowrap text-left`}>
                    <div className="inline-flex items-center gap-1 text-left">
                      <span
                        className={`inline-flex w-fit items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight ${statusBadge.className}`}
                      >
                        {statusBadge.label}
                      </span>
                      {hasError ? (
                        <button
                          type="button"
                          onClick={() => toggleRowExpansion(log.id)}
                          className="inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-red-600 transition hover:bg-red-50"
                          title="Ver mensagem de erro"
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className={`${BODY_CELL} max-w-[260px]`}>
                    <span className="line-clamp-2" title={log.subject_or_template}>
                      {log.subject_or_template || "—"}
                    </span>
                  </td>
                  <td className={`${BODY_CELL} whitespace-nowrap`}>
                    {formatNotificationDate(log.created_at)}
                  </td>
                </tr>
                {hasError && isExpanded ? (
                  <tr className="bg-red-50/60">
                    <td colSpan={6} className="px-3 py-2 text-xs text-red-800">
                      <span className="font-semibold">Erro: </span>
                      {log.error_message}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
